import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Space,
  Tag,
  Spin,
  Typography,
  Tooltip,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  Table,
  Popconfirm,
  message,
  Row,
  Col,
  Modal
} from 'antd';
import {
  DownOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  FormOutlined,
  FolderOutlined,
  TagsOutlined,
  EyeOutlined,
  DeleteOutlined,
  SaveOutlined,
  EditOutlined,
  UploadOutlined,
  UpOutlined,
  BookOutlined,
  KeyOutlined,
  LinkOutlined
} from '@ant-design/icons';
import {
  literatureService,
  copyLiteratureCslExportToClipboard,
  LiteratureAttachment,
  literatureHasPdfAttachment,
  mergeAttachmentsForDisplay,
  isPrimaryPdfAttachmentRow,
  type Literature,
  type UserCslStyleInfo
} from '../../services/literature/literatureService';
import type { Category } from '../../services/literature/categoryService';
import type { MenuProps } from 'antd';
import UserCslStyleModal from './UserCslStyleModal';
import { LimsNavIcon } from '../LimsNavIcon';
import RichTextEditor from '../RichTextEditor';
import { coerceNotesToHtmlForEditor } from '../../utils/literatureNotesHtml';
import { normalizeNotesHtmlToImgEmbed } from '../../utils/literatureNotesImageStub';
import '../../pages/MemberManagement.css';
import '../../styles/literature-lims.css';
import '../../styles/literature-stitch.css';
import '../../styles/pdf-reader-stitch.css';

function getRelatedIdsFromLiterature(lit: Literature | null): string[] {
  if (!lit) return [];
  if (Array.isArray(lit.relatedLiteratures) && lit.relatedLiteratures.length) {
    return lit.relatedLiteratures.map((r) => r._id);
  }
  const raw = lit.relatedLiteratureIds;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === 'string' ? x : String((x as { _id?: string })._id ?? '')))
    .filter(Boolean);
}

const SECTION_STORAGE_KEY = 'literatureInspectorSections';
const FOLD_STORAGE_KEY = 'literatureInspectorPanelFold';

export type InspectorFoldKey =
  | 'info'
  | 'abstract'
  | 'readStatus'
  | 'keywords'
  | 'classification'
  | 'attachments'
  | 'notes'
  | 'related';

const DEFAULT_FOLD_EXPANDED: Record<InspectorFoldKey, boolean> = {
  info: true,
  abstract: true,
  readStatus: true,
  keywords: true,
  classification: true,
  attachments: true,
  notes: true,
  related: true
};

function loadFoldExpanded(): Record<InspectorFoldKey, boolean> {
  const base = { ...DEFAULT_FOLD_EXPANDED };
  try {
    const raw = localStorage.getItem(FOLD_STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    (Object.keys(base) as InspectorFoldKey[]).forEach((k) => {
      if (k in parsed) base[k] = Boolean(parsed[k]);
    });
    if (!('classification' in parsed)) {
      if ('collectionsTags' in parsed) {
        base.classification = Boolean(parsed.collectionsTags);
      } else if ('collection' in parsed || 'tags' in parsed) {
        base.classification = Boolean(parsed.collection) || Boolean(parsed.tags);
      }
    }
    return base;
  } catch {
    return { ...DEFAULT_FOLD_EXPANDED };
  }
}

function saveFoldExpanded(f: Record<InspectorFoldKey, boolean>) {
  try {
    localStorage.setItem(FOLD_STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
}

export type InspectorSectionKey =
  | 'info'
  | 'abstract'
  | 'readStatus'
  | 'keywords'
  | 'attachments'
  | 'notes'
  | 'classification'
  | 'related';

const DEFAULT_SECTIONS: Record<InspectorSectionKey, boolean> = {
  info: true,
  abstract: true,
  readStatus: true,
  keywords: true,
  attachments: true,
  notes: true,
  classification: true,
  related: true
};

function loadSectionPrefs(): Record<InspectorSectionKey, boolean> {
  const next = { ...DEFAULT_SECTIONS };
  try {
    const raw = localStorage.getItem(SECTION_STORAGE_KEY);
    if (!raw) return next;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    (Object.keys(next) as InspectorSectionKey[]).forEach((k) => {
      if (k in parsed) next[k] = Boolean(parsed[k]);
    });
    if (!('classification' in parsed) && ('collections' in parsed || 'tags' in parsed)) {
      next.classification = Boolean(parsed.collections) || Boolean(parsed.tags);
    }
    if (!('related' in parsed)) next.related = DEFAULT_SECTIONS.related;
    return next;
  } catch {
    return { ...DEFAULT_SECTIONS };
  }
}

function saveSectionPrefs(sections: Record<InspectorSectionKey, boolean>) {
  try {
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(sections));
  } catch {
    /* ignore */
  }
}

function resolveLiteratureAssetUrl(raw: string): string {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/uploads')) return `/api${raw}`;
  if (raw.startsWith('/')) return `${window.location.origin}${raw}`;
  return `${window.location.origin}/${raw}`;
}

export interface LiteratureInspectorPanelProps {
  literatureId: string | null;
  categories: Category[];
  showSectionRail?: boolean;
  onNavigate?: (path: string) => void;
  onLiteratureUpdated?: () => void;
  onSelectRelated?: (id: string) => void;
  onDeleted?: () => void;
}

const RAIL: { key: InspectorSectionKey; icon: React.ReactNode; title: string }[] = [
  { key: 'info', icon: <InfoCircleOutlined />, title: '基本信息' },
  { key: 'abstract', icon: <FileTextOutlined />, title: '摘要' },
  {
    key: 'classification',
    icon: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <FolderOutlined />
        <TagsOutlined style={{ fontSize: 12 }} />
      </span>
    ),
    title: '分类（集合 + 标签）'
  },
  { key: 'readStatus', icon: <BookOutlined />, title: '阅读状态' },
  { key: 'keywords', icon: <KeyOutlined />, title: '关键词' },
  { key: 'attachments', icon: <PaperClipOutlined />, title: '附件与文件' },
  { key: 'notes', icon: <FormOutlined />, title: '笔记' },
  { key: 'related', icon: <LinkOutlined />, title: '相关文献' }
];

const LiteratureInspectorPanel: React.FC<LiteratureInspectorPanelProps> = ({
  literatureId,
  categories,
  showSectionRail = true,
  onNavigate,
  onLiteratureUpdated,
  onSelectRelated,
  onDeleted
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [literature, setLiterature] = useState<Literature | null>(null);
  const [sectionPrefs, setSectionPrefs] = useState<Record<InspectorSectionKey, boolean>>(loadSectionPrefs);
  /** 无右侧 RAIL 时用户无法再把被隐藏的区块打开，故一律显示全部；有 RAIL 时用本地偏好 */
  const sections = useMemo(
    () => (showSectionRail ? sectionPrefs : { ...DEFAULT_SECTIONS }),
    [showSectionRail, sectionPrefs]
  );
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showNotesEditorModal, setShowNotesEditorModal] = useState(false);
  const [lastNotesSavedAt, setLastNotesSavedAt] = useState<string | null>(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [userCslStyles, setUserCslStyles] = useState<UserCslStyleInfo[]>([]);
  const [cslModalOpen, setCslModalOpen] = useState(false);
  const [foldExpanded, setFoldExpanded] = useState<Record<InspectorFoldKey, boolean>>(loadFoldExpanded);
  const [relatedPickOpen, setRelatedPickOpen] = useState(false);
  const [pickKeyword, setPickKeyword] = useState('');
  const [pickOptions, setPickOptions] = useState<Literature[]>([]);
  const [pickLoading, setPickLoading] = useState(false);
  const [selectedToAddId, setSelectedToAddId] = useState<string | undefined>(undefined);
  const [savingRelated, setSavingRelated] = useState(false);

  const toggleFold = useCallback((key: InspectorFoldKey) => {
    setFoldExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveFoldExpanded(next);
      return next;
    });
  }, []);

  /** 检视侧只读摘要：与弹窗编辑同源 HTML，统一还原为 img 以便渲染图片 */
  const notesSummaryHtml = useMemo(
    () => normalizeNotesHtmlToImgEmbed(notesDraft || ''),
    [notesDraft]
  );

  const notesSummaryEmpty = useMemo(() => {
    const h = notesSummaryHtml || '';
    if (!h.trim()) return true;
    if (/<img\s/i.test(h)) return false;
    const text = h.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, '').trim();
    return !text;
  }, [notesSummaryHtml]);

  const refreshUserCslStyles = useCallback(async () => {
    try {
      const rows = await literatureService.listUserCslStyles();
      setUserCslStyles(Array.isArray(rows) ? rows : []);
    } catch {
      setUserCslStyles([]);
    }
  }, []);

  useEffect(() => {
    void refreshUserCslStyles();
  }, [refreshUserCslStyles]);

  const toggleSection = useCallback((key: InspectorSectionKey) => {
    setSectionPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveSectionPrefs(next);
      return next;
    });
  }, []);

  const fetchLiterature = useCallback(async () => {
    if (!literatureId) {
      setLiterature(null);
      return;
    }
    setLoading(true);
    try {
      const data = await literatureService.getDetail(literatureId);
      setLiterature(data);
      setNotesDraft(coerceNotesToHtmlForEditor(data.notes || ''));
      setLastNotesSavedAt(null);
      form.setFieldsValue({
        title: data.title,
        authors: data.authors?.join(', ') || '',
        journal: data.journal,
        year: data.year,
        doi: data.doi,
        pmid: data.pmid,
        articleUrl: data.articleUrl,
        volume: data.volume,
        issue: data.issue,
        pages: data.pages,
        publisher: data.publisher,
        issn: data.issn,
        isbn: data.isbn,
        docLanguage: data.docLanguage,
        abstract: data.abstract,
        keywords: data.keywords?.join(', ') || '',
        categoryId: data.categoryId || undefined,
        tags: data.tags || [],
        readStatus: data.readStatus
      });
    } catch {
      setLiterature(null);
      message.error('加载文献失败');
    } finally {
      setLoading(false);
    }
  }, [literatureId, form]);

  useEffect(() => {
    void fetchLiterature();
  }, [fetchLiterature]);

  useEffect(() => {
    if (!relatedPickOpen || !literatureId) return;
    let cancelled = false;
    setPickLoading(true);
    const t = window.setTimeout(() => {
      void literatureService
        .getList({
          page: 1,
          limit: 200,
          ...(pickKeyword.trim() ? { keyword: pickKeyword.trim() } : {}),
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })
        .then((res: { literatures?: Literature[] }) => {
          if (!cancelled) {
            setPickOptions(res.literatures || []);
            setPickLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setPickLoading(false);
        });
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [relatedPickOpen, literatureId, pickKeyword]);

  const categoryName = literature?.categoryId
    ? categories.find((c) => c._id === literature.categoryId)?.name
    : null;
  const hasPdf = literature ? literatureHasPdfAttachment(literature) : false;
  const attachmentsForDisplay = useMemo(() => mergeAttachmentsForDisplay(literature), [literature]);

  const handleSaveMeta = async () => {
    if (!literatureId || !literature) return;
    try {
      const v = await form.validateFields();
      setSavingMeta(true);
      const selectedCat = categories.find((c) => c._id === v.categoryId);
      const updated = await literatureService.update(literatureId, {
        title: v.title,
        authors: v.authors
          ? String(v.authors)
              .split(',')
              .map((a: string) => a.trim())
              .filter(Boolean)
          : [],
        journal: v.journal,
        year: v.year,
        doi: v.doi,
        pmid: v.pmid,
        articleUrl: v.articleUrl != null ? String(v.articleUrl).trim() : '',
        volume: v.volume != null ? String(v.volume).trim() : '',
        issue: v.issue != null ? String(v.issue).trim() : '',
        pages: v.pages != null ? String(v.pages).trim() : '',
        publisher: v.publisher != null ? String(v.publisher).trim() : '',
        issn: v.issn != null ? String(v.issn).trim() : '',
        isbn: v.isbn != null ? String(v.isbn).trim() : '',
        docLanguage: v.docLanguage != null ? String(v.docLanguage).trim() : '',
        abstract: v.abstract,
        keywords: v.keywords
          ? String(v.keywords)
              .split(',')
              .map((k: string) => k.trim())
              .filter(Boolean)
          : [],
        category: selectedCat?.name || '',
        categoryId: v.categoryId || undefined,
        tags: Array.isArray(v.tags) ? v.tags : [],
        readStatus: v.readStatus
      });
      setLiterature(updated);
      form.setFieldsValue({
        title: updated.title,
        authors: updated.authors?.join(', ') || '',
        journal: updated.journal,
        year: updated.year,
        doi: updated.doi,
        pmid: updated.pmid,
        articleUrl: updated.articleUrl,
        volume: updated.volume,
        issue: updated.issue,
        pages: updated.pages,
        publisher: updated.publisher,
        issn: updated.issn,
        isbn: updated.isbn,
        docLanguage: updated.docLanguage,
        abstract: updated.abstract,
        keywords: updated.keywords?.join(', ') || '',
        categoryId: updated.categoryId || undefined,
        tags: updated.tags || [],
        readStatus: updated.readStatus
      });
      message.success('已保存');
      onLiteratureUpdated?.();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  const openNotesEditor = useCallback(() => {
    setNotesDraft((prev) => normalizeNotesHtmlToImgEmbed(prev));
    setShowNotesEditorModal(true);
  }, []);

  const handleSaveNotes = async (): Promise<boolean> => {
    if (!literatureId) return false;
    setSavingNotes(true);
    try {
      const updated = await literatureService.update(literatureId, { notes: notesDraft });
      setLiterature(updated);
      setLastNotesSavedAt(
        new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
      message.success('笔记已保存');
      onLiteratureUpdated?.();
      return true;
    } catch (error: any) {
      message.error(error.message || '保存笔记失败');
      return false;
    } finally {
      setSavingNotes(false);
    }
  };

  const handleConfirmAddRelated = async () => {
    if (!literatureId || !literature || !selectedToAddId) {
      message.warning('请选择一篇文献');
      return;
    }
    setSavingRelated(true);
    try {
      const ids = Array.from(new Set([...getRelatedIdsFromLiterature(literature), selectedToAddId]));
      const updated = await literatureService.update(literatureId, { relatedLiteratureIds: ids });
      setLiterature(updated);
      message.success('已关联');
      onLiteratureUpdated?.();
      setRelatedPickOpen(false);
      setSelectedToAddId(undefined);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : '关联失败');
    } finally {
      setSavingRelated(false);
    }
  };

  const handleRemoveRelated = async (rid: string) => {
    if (!literatureId || !literature) return;
    setSavingRelated(true);
    try {
      const ids = getRelatedIdsFromLiterature(literature).filter((x) => x !== rid);
      const updated = await literatureService.update(literatureId, { relatedLiteratureIds: ids });
      setLiterature(updated);
      message.success('已移除关联');
      onLiteratureUpdated?.();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : '移除失败');
    } finally {
      setSavingRelated(false);
    }
  };

  const handleDelete = async () => {
    if (!literatureId) return;
    try {
      await literatureService.delete(literatureId);
      message.success('已删除');
      onDeleted?.();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleAddFileAttachment = async (file: File) => {
    if (!literatureId) return;
    setAttachmentBusy(true);
    try {
      const updated = await literatureService.addAttachment(literatureId, { file });
      setLiterature(updated);
      message.success('文件已上传');
      onLiteratureUpdated?.();
    } catch (error: any) {
      message.error(error.message || '上传失败');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleFetchFulltextFromDoi = async () => {
    if (!literatureId || !literature?.doi?.trim()) {
      message.warning('请先填写 DOI');
      return;
    }
    setAttachmentBusy(true);
    try {
      const r = await literatureService.fetchFulltextFromDoi(literatureId);
      setLiterature(r.literature);
      message.success('文献获取成功');
      onLiteratureUpdated?.();
    } catch {
      message.error('文献获取失败');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    if (!literatureId) return;
    setAttachmentBusy(true);
    try {
      const updated = await literatureService.removeAttachment(literatureId, attachmentId);
      setLiterature(updated);
      message.success('已删除附件');
      onLiteratureUpdated?.();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleExportDownload = useCallback(
    async (format: 'bibtex' | 'ris') => {
      if (!literatureId) return;
      try {
        await literatureService.exportLiteraturesFile([literatureId], format);
        message.success('已开始下载');
      } catch (e: any) {
        message.error(e.message || '导出失败');
      }
    },
    [literatureId]
  );

  const handleExportCopyCsl = useCallback(
    async (style: string) => {
      if (!literatureId) return;
      try {
        const data = await literatureService.exportLiteraturesCsl([literatureId], style);
        await copyLiteratureCslExportToClipboard(data);
        message.success(
          data.html?.trim()
            ? `已复制（${style}）；粘贴到 Word 等可保留斜体等格式`
            : `已复制参考文献（${style}）`
        );
      } catch (e: any) {
        if (e?.name === 'NotAllowedError') {
          message.error('无法访问剪贴板，请检查浏览器权限');
        } else {
          message.error(e.message || '复制失败');
        }
      }
    },
    [literatureId]
  );

  const singleExportMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      { key: 'bib', label: '下载 BibTeX', onClick: () => void handleExportDownload('bibtex') },
      { key: 'ris', label: '下载 RIS', onClick: () => void handleExportDownload('ris') },
      { type: 'divider' },
      {
        key: 'gb',
        label: '复制 GB/T 7714-2015（Word 可保留格式）',
        onClick: () => void handleExportCopyCsl('gb7714-numeric')
      },
      { key: 'apa', label: '复制 APA（Word 可保留格式）', onClick: () => void handleExportCopyCsl('apa') },
      { key: 'van', label: '复制 Vancouver（Word 可保留格式）', onClick: () => void handleExportCopyCsl('vancouver') },
      { key: 'harv', label: '复制 Harvard1（Word 可保留格式）', onClick: () => void handleExportCopyCsl('harvard1') }
    ];
    if (userCslStyles.length > 0) {
      items.push({ type: 'divider', key: 'cus-div' });
      items.push({
        type: 'group',
        key: 'cus-group',
        label: '已上传的 CSL',
        children: userCslStyles.map((s) => ({
          key: `cus-${s.styleKey}`,
          label: `复制（${s.label || s.styleKey}）`,
          onClick: () => void handleExportCopyCsl(s.styleKey)
        }))
      });
    }
    items.push({ type: 'divider', key: 'manage-div' });
    items.push({
      key: 'manage-csl',
      label: '管理自定义 CSL…',
      onClick: () => setCslModalOpen(true)
    });
    return items;
  }, [userCslStyles, handleExportDownload, handleExportCopyCsl]);

  const stitchToolbar =
    literatureId && literature && !loading ? (
      <div className="lit-inspector-stitch-toolbar">
        <div className="lit-inspector-stitch-toolbar__primary">
          <Button
            type="primary"
            className="lit-stitch-btn-primary"
            icon={<SaveOutlined />}
            loading={savingMeta}
            onClick={() => void handleSaveMeta()}
          >
            保存元数据
          </Button>
          {hasPdf ? (
            <Button
              className="lit-stitch-btn-secondary"
              icon={<EyeOutlined />}
              onClick={() => onNavigate?.(`/literature/${literature._id}/view`)}
            >
              查看 PDF
            </Button>
          ) : null}
        </div>
        <div className="lit-inspector-stitch-toolbar__icons">
          <Dropdown menu={{ items: singleExportMenuItems }} trigger={['click']}>
            <Button
              type="text"
              title="导出引用"
              icon={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <LimsNavIcon name="format_quote" sizePx={20} />
                  <DownOutlined style={{ fontSize: 10, opacity: 0.75 }} />
                </span>
              }
            />
          </Dropdown>
          <Popconfirm title="确定删除该文献？" onConfirm={() => void handleDelete()} okText="确定" cancelText="取消">
            <Button type="text" danger icon={<DeleteOutlined />} title="删除文献" />
          </Popconfirm>
        </div>
      </div>
    ) : null;

  const content = (
    <div
      className={`lit-inspector-stitch-scroll${showSectionRail ? ' lit-inspector-content--rail' : ''}`}
    >
      {!literatureId ? (
        <Typography.Text type="secondary">在列表中点击一条文献，在此查看与编辑。</Typography.Text>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !literature ? (
        <Typography.Text type="danger">加载失败或文献不存在</Typography.Text>
      ) : (
        <>
          <div className="lit-inspector-lit-title">{literature.title}</div>

          <div className="lit-inspector-peer-stack">
          <Form form={form} layout="vertical" size="small" requiredMark className="lit-inspector-stitch">
            <div style={{ display: sections.info ? 'block' : 'none', marginBottom: 16 }} className="lit-stitch-section lit-stitch-panel">
              <button
                type="button"
                className="lit-stitch-section-head lit-stitch-section-head--clickable"
                onClick={() => toggleFold('info')}
              >
                <div className="lit-stitch-section-head__left">
                  <LimsNavIcon name="info" sizePx={20} />
                  <span className="lit-stitch-section-title">基本信息</span>
                </div>
                {foldExpanded.info ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
              </button>
              {foldExpanded.info ? (
              <div style={{ marginTop: 4 }}>
                <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Row gutter={[14, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="authors" label="作者" tooltip="逗号分隔">
                      <Input placeholder="作者1, 作者2" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="journal"
                      label="期刊"
                      tooltip="与 CSL container-title 对齐；导入 RIS/BibTeX 时自动合并多条 CSL 出版物题名类字段。"
                    >
                      <Input placeholder="期刊 / 容器题名" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[14, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="year" label="年份">
                      <InputNumber min={1900} max={2100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={16}>
                    <Form.Item name="doi" label="DOI">
                      <Input placeholder="10.xxxx/…" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="pmid" label="PMID">
                  <Input />
                </Form.Item>
                <Form.Item
                  name="articleUrl"
                  label="文献网址"
                  extra={
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      期刊/出版社文章页等（书目信息，非附件）。
                    </Typography.Text>
                  }
                >
                  <Input placeholder="https://…" />
                </Form.Item>
                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                  卷期 / 页码 / 出版社 / ISSN·ISBN / 语言（与 RIS、BibTeX 对应）
                </Typography.Text>
                <Row gutter={[12, 0]}>
                  <Col xs={8} sm={8}>
                    <Form.Item name="volume" label="卷">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={8}>
                    <Form.Item name="issue" label="期">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={8}>
                    <Form.Item name="pages" label="页码">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="publisher" label="出版社">
                  <Input />
                </Form.Item>
                <Row gutter={[14, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="issn" label="ISSN">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="isbn" label="ISBN">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="docLanguage" label="语言">
                  <Input placeholder="如 en、zh" />
                </Form.Item>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  上传于 {new Date(literature.createdAt).toLocaleString('zh-CN')} · 更新于{' '}
                  {new Date(literature.updatedAt).toLocaleString('zh-CN')}
                </Typography.Text>
              </div>
              ) : null}
            </div>

            <div style={{ display: sections.abstract ? 'block' : 'none', marginBottom: 16 }} className="lit-stitch-section lit-stitch-panel">
              <button
                type="button"
                className="lit-stitch-section-head lit-stitch-section-head--clickable"
                onClick={() => toggleFold('abstract')}
              >
                <div className="lit-stitch-section-head__left">
                  <LimsNavIcon name="description" sizePx={20} />
                  <span className="lit-stitch-section-title">摘要</span>
                </div>
                {foldExpanded.abstract ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
              </button>
              {foldExpanded.abstract ? (
                <>
                  <div className="lit-stitch-abstract-box">
                    <Form.Item name="abstract" label={null} style={{ marginBottom: 8 }}>
                      <Input.TextArea rows={6} placeholder="摘要" />
                    </Form.Item>
                  </div>
                  <Button type="link" size="small" onClick={() => void handleSaveMeta()} style={{ paddingLeft: 0 }}>
                    保存摘要（与元数据一并保存）
                  </Button>
                </>
              ) : null}
            </div>

            {sections.classification ? (
              <div style={{ display: 'block', marginBottom: 16 }} className="lit-stitch-section lit-stitch-panel">
                <button
                  type="button"
                  className="lit-stitch-section-head lit-stitch-section-head--clickable"
                  onClick={() => toggleFold('classification')}
                >
                  <div className="lit-stitch-section-head__left">
                    <LimsNavIcon name="folder" sizePx={20} />
                    <span className="lit-stitch-section-title">分类（集合 + 标签）</span>
                  </div>
                  {foldExpanded.classification ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
                </button>
                {foldExpanded.classification ? (
                  <>
                    <Form.Item name="categoryId" label="文库 / 集合">
                      <Select allowClear placeholder="未分类" options={categories.map((c) => ({ label: c.name, value: c._id }))} />
                    </Form.Item>
                    {categoryName ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                        当前名称：{categoryName}
                      </Typography.Text>
                    ) : null}
                    <Form.Item name="tags" label="标签">
                      <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[',']} />
                    </Form.Item>
                  </>
                ) : null}
              </div>
            ) : null}

            {sections.readStatus ? (
              <div style={{ display: 'block', marginBottom: 16 }} className="lit-stitch-section lit-stitch-panel">
                <button
                  type="button"
                  className="lit-stitch-section-head lit-stitch-section-head--clickable"
                  onClick={() => toggleFold('readStatus')}
                >
                  <div className="lit-stitch-section-head__left">
                    <LimsNavIcon name="menu_book" sizePx={20} />
                    <span className="lit-stitch-section-title">阅读状态</span>
                  </div>
                  {foldExpanded.readStatus ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
                </button>
                {foldExpanded.readStatus ? (
                  <Form.Item name="readStatus" label={null}>
                    <Select
                      placeholder="选择阅读状态"
                      options={[
                        { value: '未读', label: '未读' },
                        { value: '阅读中', label: '阅读中' },
                        { value: '已读', label: '已读' },
                        { value: '精读', label: '精读' }
                      ]}
                    />
                  </Form.Item>
                ) : null}
              </div>
            ) : null}

            {sections.keywords ? (
              <div style={{ display: 'block', marginBottom: 16 }} className="lit-stitch-section lit-stitch-panel">
                <button
                  type="button"
                  className="lit-stitch-section-head lit-stitch-section-head--clickable"
                  onClick={() => toggleFold('keywords')}
                >
                  <div className="lit-stitch-section-head__left">
                    <LimsNavIcon name="label" sizePx={20} />
                    <span className="lit-stitch-section-title">关键词</span>
                  </div>
                  {foldExpanded.keywords ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
                </button>
                {foldExpanded.keywords ? (
                  <Form.Item name="keywords" label={null} tooltip="逗号分隔">
                    <Input placeholder="关键词1, 关键词2" />
                  </Form.Item>
                ) : null}
              </div>
            ) : null}

          {sections.attachments && (
            <section className="lit-stitch-section lit-stitch-panel" style={{ marginBottom: 16 }}>
              <button
                type="button"
                className="lit-stitch-section-head lit-stitch-section-head--clickable"
                onClick={() => toggleFold('attachments')}
              >
                <div className="lit-stitch-section-head__left">
                  <LimsNavIcon name="attachment" sizePx={20} />
                  <span className="lit-stitch-section-title">附件与文件</span>
                </div>
                {foldExpanded.attachments ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
              </button>
              {foldExpanded.attachments ? (
                <>
                  <div className="lit-stitch-attach-fetch-row">
                    <Tooltip title="需文献含 DOI；使用个人资料中的联系邮箱作为 Unpaywall 标识">
                      <Button
                        type="default"
                        className="lit-stitch-fetch-pill lit-stitch-fetch-pill--block"
                        block
                        icon={<LimsNavIcon name="bolt" sizePx={14} />}
                        onClick={() => void handleFetchFulltextFromDoi()}
                        loading={attachmentBusy}
                        disabled={!literature?.doi?.trim()}
                      >
                        从 DOI 获取全文
                      </Button>
                    </Tooltip>
                  </div>
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
                    PDF 与其它文件均列在附件中。多 PDF 时在「查看 PDF」阅读器顶部切换。DOI 拉取依赖个人资料邮箱与管理员解析配置。
                  </Typography.Paragraph>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div className="lit-stitch-attach-table-wrap">
                <Table<LiteratureAttachment>
                  size="small"
                  rowKey={(row) => String(row._id || row.url)}
                  pagination={false}
                  dataSource={attachmentsForDisplay}
                  columns={[
                    {
                      title: '名称',
                      dataIndex: 'name',
                      key: 'name',
                      ellipsis: true,
                      render: (name: string, row) => (
                        <Space size={4} wrap>
                          <Typography.Text ellipsis={{ tooltip: name }}>{name}</Typography.Text>
                          {literature && isPrimaryPdfAttachmentRow(literature, row) ? (
                            <Tag color="blue">主 PDF</Tag>
                          ) : null}
                        </Space>
                      )
                    },
                    {
                      title: '类型',
                      dataIndex: 'type',
                      key: 'type',
                      width: 64,
                      render: (t: string) => (t === 'link' ? '链接' : '文件')
                    },
                    {
                      title: '操作',
                      key: 'actions',
                      width: 220,
                      render: (_, row) => {
                        const openUrl = resolveLiteratureAssetUrl(row.url);
                        const isPdfish =
                          row.mimeType === 'application/pdf' ||
                          /\.pdf$/i.test(row.name || '') ||
                          /\.pdf(\?|$)/i.test(row.url || '');
                        const isLegacyPrimaryPdf = String(row._id) === 'legacy-primary-pdf';
                        const canDeleteAttachment = row._id && !isLegacyPrimaryPdf;
                        return (
                          <Space size="small" wrap className="lit-stitch-attach-actions">
                            <Button
                              type="default"
                              size="small"
                              className="lit-stitch-btn-secondary"
                              onClick={() => window.open(openUrl, '_blank', 'noopener,noreferrer')}
                            >
                              打开
                            </Button>
                            {row.type === 'file' && isPdfish && (
                              <Button
                                type="default"
                                size="small"
                                className="lit-stitch-btn-secondary"
                                icon={<EyeOutlined />}
                                onClick={() => window.open(openUrl, '_blank', 'noopener,noreferrer')}
                              >
                                预览
                              </Button>
                            )}
                            <Popconfirm
                              title="删除该附件？"
                              onConfirm={() => canDeleteAttachment && handleRemoveAttachment(String(row._id))}
                              disabled={!canDeleteAttachment}
                            >
                              <Button
                                type="default"
                                danger
                                size="small"
                                className="lit-stitch-btn-secondary"
                                disabled={!canDeleteAttachment || attachmentBusy}
                              >
                                删除
                              </Button>
                            </Popconfirm>
                          </Space>
                        );
                      }
                    }
                  ]}
                />
                </div>
                    <div className="lit-stitch-attach-footer">
                      <Upload
                        beforeUpload={(file) => {
                          void handleAddFileAttachment(file);
                          return false;
                        }}
                        showUploadList={false}
                        accept=".pdf,.doc,.docx"
                        disabled={attachmentBusy}
                      >
                        <Button className="lit-stitch-btn-secondary" icon={<UploadOutlined />} loading={attachmentBusy}>
                          上传
                        </Button>
                      </Upload>
                    </div>
                  </Space>
                </>
              ) : null}
            </section>
          )}

          {sections.notes && (
            <section className="lit-stitch-section lit-stitch-panel" style={{ marginBottom: 16 }}>
              <button
                type="button"
                className="lit-stitch-section-head lit-stitch-section-head--clickable"
                onClick={() => toggleFold('notes')}
              >
                <div className="lit-stitch-section-head__left">
                  <LimsNavIcon name="edit_note" sizePx={20} />
                  <span className="lit-stitch-section-title">笔记</span>
                </div>
                {foldExpanded.notes ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
              </button>
              {foldExpanded.notes ? (
                <div className="lit-stitch-notes-panel-wrap">
                  <div className="lit-stitch-notes-summary-card">
                    <div className="lit-stitch-notes-summary-head">笔记预览</div>
                    {notesSummaryEmpty ? (
                      <p className="lit-stitch-notes-summary-empty">暂无笔记内容</p>
                    ) : (
                      <div className="lit-stitch-notes-summary-body" dangerouslySetInnerHTML={{ __html: notesSummaryHtml }} />
                    )}
                  </div>
                  <div className="lit-pdf-notes-card">
                    <div className="lit-pdf-notes-actions">
                      <button type="button" className="lit-pdf-btn-primary" onClick={openNotesEditor}>
                        <EditOutlined /> 编辑笔记
                      </button>
                      <button type="button" className="lit-pdf-btn-primary" disabled={savingNotes} onClick={() => void handleSaveNotes()}>
                        {savingNotes ? '保存中…' : '保存笔记'}
                      </button>
                    </div>
                    <div className="lit-pdf-notes-meta">
                      <span className="lit-pdf-autosave-hint">
                        {lastNotesSavedAt ? `上次保存 ${lastNotesSavedAt}` : '尚未保存到服务器'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          )}

          {sections.related && (
            <section className="lit-stitch-section lit-stitch-panel" style={{ marginBottom: 16 }}>
              <button
                type="button"
                className="lit-stitch-section-head lit-stitch-section-head--clickable"
                onClick={() => toggleFold('related')}
              >
                <div className="lit-stitch-section-head__left">
                  <LinkOutlined style={{ fontSize: 18, color: 'var(--st-primary)' }} />
                  <span className="lit-stitch-section-title">相关文献</span>
                </div>
                {foldExpanded.related ? <UpOutlined className="lit-stitch-chevron" /> : <DownOutlined className="lit-stitch-chevron" />}
              </button>
              {foldExpanded.related ? (
                <div className="lit-stitch-related-wrap">
                  <p className="lit-stitch-related-hint">
                    与文献库中其他条目互链后，在双方检视页的「相关文献」中均可看到对方。
                  </p>
                  <div className="lit-stitch-related-list">
                    {(literature?.relatedLiteratures || []).length === 0 ? (
                      <p className="lit-stitch-related-empty">暂无关联文献</p>
                    ) : (
                      (literature?.relatedLiteratures || []).map((r) => (
                        <div key={r._id} className="lit-stitch-related-row">
                          <div className="lit-stitch-related-row__main">
                            <div className="lit-stitch-related-row__title">{r.title}</div>
                            <div className="lit-stitch-related-row__meta">
                              {[r.year, r.journal].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </div>
                          <Space size={4}>
                            {onSelectRelated ? (
                              <Button type="link" size="small" disabled={savingRelated} onClick={() => onSelectRelated(r._id)}>
                                打开
                              </Button>
                            ) : null}
                            <Button type="link" size="small" danger disabled={savingRelated} onClick={() => void handleRemoveRelated(r._id)}>
                              移除
                            </Button>
                          </Space>
                        </div>
                      ))
                    )}
                  </div>
                  <Button
                    type="primary"
                    className="lit-stitch-btn-primary lit-stitch-related-add-btn"
                    size="small"
                    icon={<LinkOutlined />}
                    disabled={!literatureId || savingRelated}
                    onClick={() => {
                      setPickKeyword('');
                      setSelectedToAddId(undefined);
                      setRelatedPickOpen(true);
                    }}
                  >
                    添加相关文献
                  </Button>
                </div>
              ) : null}
            </section>
          )}

          </Form>
          </div>

        </>
      )}
    </div>
  );

  const rail = showSectionRail && (
    <div className="lit-inspector-stitch-rail">
      {RAIL.map(({ key, icon, title }) => (
        <Tooltip key={key} title={`${sections[key] ? '隐藏' : '显示'}：${title}`} placement="left">
          <Button
            type="text"
            size="small"
            icon={icon}
            onClick={() => toggleSection(key)}
            style={{
              color: sections[key] ? 'var(--ant-color-primary)' : 'rgba(0,0,0,0.35)',
              width: 36,
              height: 36
            }}
          />
        </Tooltip>
      ))}
    </div>
  );

  return (
    <div
      className="members-lims literature-lims-module lit-inspector-root lit-inspector-stitch"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 320 }}
    >
      {stitchToolbar}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
        {content}
        {rail}
      </div>
      <UserCslStyleModal
        open={cslModalOpen}
        onClose={() => setCslModalOpen(false)}
        onStylesChanged={() => void refreshUserCslStyles()}
      />

      <Modal
        title="从文献库添加相关文献"
        open={relatedPickOpen}
        destroyOnHidden
        onCancel={() => setRelatedPickOpen(false)}
        okText="添加"
        confirmLoading={savingRelated}
        okButtonProps={{ disabled: !selectedToAddId }}
        onOk={() => void handleConfirmAddRelated()}
        rootClassName="lit-stitch-modal"
      >
        <p className="lit-stitch-related-pick-hint">仅可选择您本账号下的文献；保存后双方条目会互相出现在「相关文献」中。</p>
        <Select
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="输入关键词搜索标题、作者、期刊…"
          filterOption={false}
          onSearch={(v) => setPickKeyword(v)}
          loading={pickLoading}
          value={selectedToAddId}
          onChange={(v) => setSelectedToAddId(v)}
          options={pickOptions
            .filter((l) => l._id !== literatureId && !getRelatedIdsFromLiterature(literature).includes(l._id))
            .map((l) => ({
              value: l._id,
              label: `${l.title}${l.year != null ? ` (${l.year})` : ''}`
            }))}
          notFoundContent={pickLoading ? <Spin size="small" /> : '无匹配文献'}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <FileTextOutlined /> 编辑笔记
          </Space>
        }
        open={showNotesEditorModal}
        destroyOnHidden
        rootClassName="lit-inspector-notes-modal-root"
        onCancel={() => setShowNotesEditorModal(false)}
        width={720}
        footer={
          <Space>
            <Button onClick={() => setShowNotesEditorModal(false)}>关闭</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingNotes}
              onClick={async () => {
                const ok = await handleSaveNotes();
                if (ok) setShowNotesEditorModal(false);
              }}
            >
              保存
            </Button>
          </Space>
        }
      >
        <div className="lit-pdf-notes-modal-editor-wrap">
          <RichTextEditor
            value={notesDraft}
            onChange={setNotesDraft}
            imageDisplayMode="inline"
            placeholder="随时记录阅读笔记，支持加粗、列表、链接与颜色等格式…"
            height={400}
            showImage
          />
        </div>
      </Modal>
    </div>
  );
};

export default LiteratureInspectorPanel;
